import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useListSiteTasksQuery, useGetTaskStatisticsQuery } from '../../../redux/api/tasksApi';
import type { TaskStatus, TaskPriority } from '../../../redux/api/tasksApi';
import { SEVERITY_HEX_COLORS } from '../../../constants/eventStyles';

interface TasksModuleProps {
  siteId: string;
  onTaskClick?: (taskId: string) => void;
}

const PRIORITY_COLORS: Record<number, string> = {
  1: '#EF4444',
  2: '#F97316',
  3: '#F59E0B',
  4: '#3B82F6',
  5: '#9CA3AF',
};

const PRIORITY_LABELS: Record<number, string> = {
  1: 'P1', 2: 'P2', 3: 'P3', 4: 'P4', 5: 'P5',
};

const STATUS_FILTERS: { value: TaskStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'blocked', label: 'Blocked' },
];

function formatDueDate(dueDate: string | null): { text: string; isOverdue: boolean } {
  if (!dueDate) return { text: '', isOverdue: false };
  const due = new Date(dueDate);
  const now = new Date();
  const diff = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  if (diff < 0) return { text: `${Math.abs(diff)}d overdue`, isOverdue: true };
  if (diff === 0) return { text: 'Due today', isOverdue: false };
  return { text: `Due in ${diff}d`, isOverdue: false };
}

const TasksModule: React.FC<TasksModuleProps> = ({ siteId, onTaskClick }) => {
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { data: tasksData, isLoading: tasksLoading } = useListSiteTasksQuery({
    siteId,
    limit: 100,
    status: statusFilter === 'all' ? undefined : statusFilter,
    search: searchTerm || undefined,
  });

  const { data: stats } = useGetTaskStatisticsQuery({ siteId });

  const tasks = useMemo(() => {
    return tasksData?.items || [];
  }, [tasksData]);

  const overdue = stats?.overdue || 0;
  const total = stats?.total || tasks.length;
  const byStatus = stats?.by_status || {};
  const open = (byStatus['pending'] || 0) + (byStatus['assigned'] || 0) + (byStatus['in_progress'] || 0);

  return (
    <div className="space-y-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex gap-1">
          {STATUS_FILTERS.map(sf => (
            <button
              key={sf.value}
              onClick={() => setStatusFilter(sf.value)}
              className="px-2.5 py-1 rounded text-xs font-medium transition-all"
              style={{
                backgroundColor: statusFilter === sf.value ? 'rgba(23, 196, 128, 0.15)' : 'transparent',
                color: statusFilter === sf.value ? '#17c480' : 'var(--color-text-muted)',
              }}
            >
              {sf.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[120px]">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3" style={{ color: 'var(--color-text-muted)' }} />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="eg-input text-xs py-1 pl-7 w-full"
          />
        </div>
      </div>

      {/* Tasks list */}
      {tasksLoading ? (
        <div className="eg-loading text-sm">Loading tasks...</div>
      ) : tasks.length === 0 ? (
        <p className="text-sm py-4 text-center" style={{ color: 'var(--color-text-muted)' }}>No tasks found</p>
      ) : (
        <div className="space-y-1.5">
          {tasks.map(task => {
            const { text: dueText, isOverdue } = formatDueDate(task.due_date);
            const priorityColor = PRIORITY_COLORS[task.priority] || '#9CA3AF';
            const isCompleted = task.status === 'completed' || task.status === 'cancelled';

            return (
              <button
                key={task.id}
                onClick={() => onTaskClick?.(task.id)}
                className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-lg transition-colors hover:bg-[var(--color-surface-hover)]"
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: isCompleted ? '#22C55E' : priorityColor }}
                />
                <span
                  className={`text-sm flex-1 truncate ${isCompleted ? 'line-through' : ''}`}
                  style={{ color: isCompleted ? 'var(--color-text-muted)' : 'var(--color-text-primary)' }}
                >
                  {task.title}
                </span>
                {dueText && (
                  <span
                    className="text-xs flex-shrink-0"
                    style={{ color: isOverdue ? '#EF4444' : 'var(--color-text-muted)' }}
                  >
                    {dueText}
                  </span>
                )}
                <span
                  className="text-[10px] font-semibold flex-shrink-0 px-1.5 py-0.5 rounded"
                  style={{
                    backgroundColor: `${priorityColor}20`,
                    color: priorityColor,
                  }}
                >
                  {PRIORITY_LABELS[task.priority] || `P${task.priority}`}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Summary */}
      <p className="text-xs pt-1" style={{ color: 'var(--color-text-muted)' }}>
        {overdue > 0 && <span style={{ color: '#EF4444' }}>{overdue} overdue</span>}
        {overdue > 0 && ' · '}
        {open} open · {total} total
      </p>
    </div>
  );
};

export default TasksModule;
